import LoadingBox from '../../components/LoadingBox';
import React, { useContext, useReducer, useState, useEffect } from 'react';
import { Store } from '../../Store';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import userService from '../../services/user.service';
import authService from '../../services/auth.service';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import PageHeader from '../../components/shared/PageHeader';
import ConfirmModal from '../../components/ConfirmModal';
import { USER_ROLE, flagsFromUserRole, userRoleFromFlags } from '../../utils/userRole';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false };
    default:
      return state;
  }
};

function EditUser() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();

  const { id } = params;
  const [{ loading }, dispatch] = useReducer(reducer, {
    loading: true,
  });

  const { state } = useContext(Store);
  const { userInfo } = state;
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [userRole, setUserRole] = useState(USER_ROLE.CLIENT);
  const [showModal, setShowModal] = useState(false);

  const handleClose = () => setShowModal(false);
  const handleShow = () => setShowModal(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const data = await userService.getProfile(id, userInfo.token);
        setNombre(data.nombre);
        setEmail(data.email);
        setUserRole(
          userRoleFromFlags({
            isAdmin: data.isAdmin,
            isSuperAdmin: data.isSuperAdmin,
            canVerify: data.canVerify,
          })
        );
        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        toast.error(getError(err));
        dispatch({ type: 'FETCH_FAIL' });
      }
    };
    fetchData();
  }, [id, userInfo.token]);

  const handleSubmit = event => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      toast.error(t('common.fillRequiredFields', 'Completá los campos obligatorios'));
      return;
    }
    handleShow();
  };

  const editUserHandler = async () => {
    handleClose();
    try {
      const { isAdmin, isSuperAdmin, canVerify } = flagsFromUserRole(userRole);
      await userService.updateProfile(
        id,
        {
          nombre,
          email,
          isAdmin,
          isSuperAdmin,
          canVerify,
        },
        userInfo.token
      );
      toast.success(t('auth.userUpdatedSuccess'));
      navigate('/AdminScreen/users');
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const handleResetPassword = async () => {
    try {
      await authService.forgotPassword(email);
      toast.success(t('common.success'));
    } catch (err) {
      toast.error(getError(err));
    }
  };

  return (
    <div className='max-w-2xl mx-auto space-y-6 p-6'>
      {loading && <LoadingBox overlay={true} />}
      <ConfirmModal
        show={showModal}
        title={t('user.editUser')}
        message={t('common.confirmSaveChanges')}
        onConfirm={editUserHandler}
        onCancel={handleClose}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
      />
      <Helmet>
        <title>{t('user.editUser')}</title>
      </Helmet>

      <PageHeader
        title={t('user.editUser')}
        actions={
          <Button variant='outline' size='sm' onClick={() => navigate('/AdminScreen/users')}>
            <ArrowLeft className='h-4 w-4' />
            {t('common.back')}
          </Button>
        }
      />

      <Card>
        <CardContent className='pt-6'>
          <form noValidate onSubmit={handleSubmit} className='space-y-4'>
            <div className='space-y-1'>
              <label htmlFor='nombre' className='text-sm font-medium text-foreground'>
                {t('auth.name')}
              </label>
              <input
                id='nombre'
                type='text'
                required
                value={nombre}
                onChange={e => setNombre(e.target.value)}
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
              />
            </div>

            <div className='space-y-1'>
              <label htmlFor='email' className='text-sm font-medium text-foreground'>
                {t('auth.email')}
              </label>
              <input
                id='email'
                type='email'
                required
                value={email}
                onChange={e => setEmail(e.target.value.toLowerCase())}
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
              />
            </div>

            <div className='space-y-1'>
              <label htmlFor='userRole' className='text-sm font-medium text-foreground'>
                {t('user.role')}
              </label>
              <select
                id='userRole'
                value={userRole}
                onChange={e => setUserRole(e.target.value)}
                disabled={!userInfo.isSuperAdmin}
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60'
              >
                <option value={USER_ROLE.CLIENT}>{t('user.roleClient')}</option>
                <option value={USER_ROLE.ADMIN}>{t('user.roleAdmin')}</option>
                <option value={USER_ROLE.ADMIN_VERIFY}>{t('user.roleAdminVerify')}</option>
                <option value={USER_ROLE.SUPER_ADMIN}>{t('user.roleSuperAdmin')}</option>
              </select>
            </div>

            <div className='flex flex-col gap-2'>
              <Button type='submit' className='w-full'>
                {t('user.updateUser')}
              </Button>
              <Button
                type='button'
                variant='secondary'
                className='w-full'
                onClick={handleResetPassword}
              >
                {t('auth.resetPassword')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default EditUser;
